// src/pages/Counselling/Cases/[CaseDetails]/Tabs/ImportFromRequestModal.tsx
/**
 * Copies the people staged on the originating care request onto the case.
 *
 * ⚠️ **The backend does the import** (`careCase.importFromRequest`, SDK 0.0.58). This file only picks
 * *which* subjects to send. That matters: the previous client-side version created each missing member
 * itself, which meant guessing a member **type** and could only carry the fields it happened to map.
 * The endpoint match-or-creates from the subject's full details — name, email, phone, gender, DOB,
 * member status — and is **idempotent**, so anyone already on the case is skipped rather than
 * duplicated.
 *
 * ⚠️ Only offered when `case.requestID` is set. A case created by hand has no request to import from.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Center, Checkbox, Group, Loader, Modal, Stack, Text,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconUsers } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useCareCaseService, useCareRequestService,
  type CareRequestModelView,
} from '@actschurch/ccms-sdk';

/** One importable person, flattened from whichever shape the request staged them in. */
interface ImportablePerson {
  key: string;
  /**
   * The subject id the endpoint addresses people by.
   *
   * ⚠️ Null for the on-behalf and requester rows, which come from the request's top-level fields
   * rather than from `subjects[]`. They are imported with the endpoint's third argument
   * (`includeRequester`) instead — see `handleImport`.
   */
  subjectID: number | null;
  firstName: string;
  surname: string;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  role?: string | null;
  isMain: boolean;
  /** Null when the request never matched them to a member. */
  memberID: number | null;
  memberName?: string | null;
}

export interface ImportFromRequestModalProps {
  caseID: number;
  requestID: number;
  /** Member ids already on the case, so people are not offered twice. */
  existingMemberIDs: number[];
  opened: boolean;
  onClose: () => void;
  /** Called after at least one person was attached, so the caller can refetch. */
  onImported: () => void | Promise<void>;
}

/** Everyone the request names, in the same order of preference the request detail page uses. */
function peopleFromRequest(request: CareRequestModelView): ImportablePerson[] {
  const subjects = request.subjects ?? [];

  if (subjects.length > 0) {
    return subjects.map((s) => ({
      key: `subject-${s.subjectID}`,
      subjectID: s.subjectID,
      firstName: (s.firstName ?? '').trim(),
      surname: (s.surname ?? '').trim(),
      email: s.email,
      phone: s.phone,
      gender: s.gender,
      dateOfBirth: s.dateOfBirth,
      role: s.role,
      isMain: s.isMain,
      memberID: s.memberID ?? null,
      memberName: s.memberName,
    }));
  }

  // ⚠️ No staged subjects. Who the counselee is depends on `isForSelf`, not on the list being empty:
  // an on-behalf-of request names them in the top-level subject block instead.
  const onBehalfName = (request.subjectFullName
    || [request.subjectFirstName, request.subjectSurname].filter(Boolean).join(' ')).trim();

  if (request.isForSelf === false && onBehalfName) {
    return [{
      key: 'on-behalf',
      subjectID: null,
      firstName: (request.subjectFirstName ?? onBehalfName.split(' ')[0] ?? '').trim(),
      surname: (request.subjectSurname ?? onBehalfName.split(' ').slice(1).join(' ')).trim(),
      // ⚠️ The on-behalf block carries no contact details on the model view, so this row shows a
      // name only. The server still imports from whatever it holds internally.
      isMain: true,
      memberID: null,
    }];
  }

  return [{
    key: 'requester',
    subjectID: null,
    firstName: (request.firstName ?? '').trim(),
    surname: (request.surname ?? '').trim(),
    email: request.email,
    phone: request.phone,
    gender: request.gender,
    isMain: true,
    memberID: request.matchedMemberID ?? null,
    memberName: request.matchedMemberName,
  }];
}

const displayName = (p: ImportablePerson) =>
  [p.firstName, p.surname].filter(Boolean).join(' ').trim() || 'Unnamed';

export default function ImportFromRequestModal({
  caseID, requestID, existingMemberIDs, opened, onClose, onImported,
}: ImportFromRequestModalProps) {
  const requestService = useCareRequestService();
  const careCaseService = useCareCaseService();

  const [request, setRequest] = useState<CareRequestModelView | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!opened) return;
    setLoading(true);
    setFailed(false);
    try {
      setRequest(await requestService.getById(requestID));
    } catch (e) {
      console.error('[ImportFromRequest] failed to load the request:', e);
      setRequest(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [opened, requestID, requestService]);

  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => (request ? peopleFromRequest(request) : []), [request]);

  const alreadyOnCase = useCallback(
    (p: ImportablePerson) => p.memberID != null && existingMemberIDs.includes(p.memberID),
    [existingMemberIDs],
  );

  /** Everyone not already attached — the only rows that can be ticked. */
  const importable = useMemo(() => people.filter((p) => !alreadyOnCase(p)), [people, alreadyOnCase]);

  // Pre-tick everything importable: the admin opened this to fix a gap, not to browse.
  useEffect(() => {
    if (!opened) return;
    setSelected(Object.fromEntries(importable.map((p) => [p.key, true])));
  }, [opened, importable]);

  const chosen = importable.filter((p) => selected[p.key]);
  /** Rows the endpoint addresses by id. */
  const addressable = chosen.filter((p) => p.subjectID != null);
  /**
   * ⚠️ `peopleFromRequest` returns a fallback row **only** when `subjects[]` is empty, so a list is
   * either all-addressable subjects or the single fallback person. That is what makes it safe to omit
   * the id array below rather than risk "import everything missing" sweeping up others.
   */
  const wantsRequester = chosen.some((p) => p.subjectID == null);
  const canImport = (addressable.length > 0 || wantsRequester) && !busy;

  const handleImport = async () => {
    if (!canImport) return;
    setBusy(true);
    try {
      // ⚠️ Send ids explicitly when there are any — passing nothing means "import everything missing",
      // a different action from "import the ones I ticked". The fallback person has no id, so they
      // ride on the third argument; see `wantsRequester` for why the two cases never overlap.
      const added = await careCaseService.importFromRequest(
        caseID,
        addressable.length > 0 ? addressable.map((p) => p.subjectID as number) : undefined,
        wantsRequester,
      );
      // ⚠️ Read through the envelope, same as elsewhere in this app.
      const list = Array.isArray(added)
        ? added
        : (added as unknown as { data?: unknown[] })?.data;
      const count = Array.isArray(list) ? list.length : 0;

      await onImported();

      if (count > 0) {
        toast.success('Counselees imported', `${count} added to the case with their full details.`);
        onClose();
      } else {
        // ⚠️ Not success. Zero can mean "already attached" or "accepted and did nothing", and the
        // client cannot tell them apart — so say what is actually known rather than implying it worked.
        toast.warning(
          'Nothing was imported',
          'The server accepted the request but added nobody. They may already be on the case, or the '
          + 'request may have no subject record for them.',
        );
      }
    } catch (e) {
      console.error('[ImportFromRequest] import failed:', e);
      toast.error('Could not import', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title="Import counselees from the request"
      size="lg"
      centered
    >
      {loading ? (
        <Center py="xl"><Loader /></Center>
      ) : failed ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            Could not load request #{requestID}. It may have been removed, or you may not have access
            to it.
          </Text>
        </Alert>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            From {request?.reference ?? `request #${requestID}`}
            {request?.needName ? ` · ${request.needName}` : ''}.
          </Text>

          {people.length === 0 ? (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">This request names nobody to import.</Text>
            </Alert>
          ) : (
            <>
              <Stack gap="xs">
                {people.map((p) => {
                  const onCase = alreadyOnCase(p);
                  return (
                    <Card key={p.key} withBorder radius="md" padding="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <Checkbox
                            checked={onCase ? true : Boolean(selected[p.key])}
                            disabled={onCase || busy}
                            onChange={(e) => setSelected((prev) => ({
                              ...prev, [p.key]: e.currentTarget.checked,
                            }))}
                          />
                          <div style={{ minWidth: 0 }}>
                            <Group gap={6}>
                              <Text size="sm" fw={600}>{displayName(p)}</Text>
                              {p.isMain && <Badge size="xs" variant="light" color="blue">Main</Badge>}
                              {p.role && <Badge size="xs" variant="outline">{p.role}</Badge>}
                            </Group>
                            <Text size="xs" c="dimmed" truncate>
                              {[p.email, p.phone].filter(Boolean).join(' · ') || 'No contact captured'}
                            </Text>
                          </div>
                        </Group>

                        <Badge
                          size="xs"
                          variant="light"
                          color={onCase ? 'gray' : p.memberID != null ? 'teal' : 'orange'}
                          style={{ flexShrink: 0 }}
                        >
                          {onCase
                            ? 'Already on case'
                            : p.memberID != null
                              ? `Member #${p.memberID}`
                              : 'Will be created'}
                        </Badge>
                      </Group>
                    </Card>
                  );
                })}
              </Stack>



              {importable.length === 0 && (
                <Alert color="teal" variant="light" icon={<IconUsers size={16} />}>
                  <Text size="sm">Everyone on this request is already on the case.</Text>
                </Alert>
              )}
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={handleImport} loading={busy} disabled={!canImport}>
              {chosen.length > 0 ? `Import ${chosen.length}` : 'Import'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
